import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { NZ_MODAL_DATA, NzModalRef } from 'ng-zorro-antd/modal';
import { TenantService, TenantsResponse } from 'app/services/tenant/tenant.service';
import { AuthService } from 'app/services/auth/auth.service';
import { firstValueFrom } from 'rxjs';
import { SelectOption } from '@gainsystems/gains-ui-commons';

@Component({
  selector: 'gains-tenant-switcher',
  standalone: false,
  templateUrl: './gains-tenant-switcher.component.html',
  styleUrl: './gains-tenant-switcher.component.scss'
})
export class GainsTenantSwitcherComponent implements OnInit {
  tenantSwitcherDescription = "Select the environment you'd like to view for this session. You can change it later in the settings if needed.";
  environmentControl = new FormControl<SelectOption | null>(null, Validators.required);
  environments: SelectOption[] = [];
  isLoading = false;

  constructor(
    @Inject(NZ_MODAL_DATA) public data: { tenantSwitcherDescription?: string; tenantsResponse: TenantsResponse },
    private readonly modal: NzModalRef,
    private readonly _tenantService: TenantService,
    private readonly _authService: AuthService
  ) {}

  ngOnInit(): void {
    this.tenantSwitcherDescription = this.data?.tenantSwitcherDescription || this.tenantSwitcherDescription;
    if (this.data?.tenantsResponse) {
      this.mapToEnvironments(this.data.tenantsResponse);
    } else {
      this.loadTenants();
    }
  }

  private mapToEnvironments(response: TenantsResponse) {
    this.environments = response.tenants.map(tenant => ({
      title: `${tenant.customerName ?? tenant.tenantId} (${tenant.accessLevel})`,
      value: tenant.tenantId
    }));

    // Session-scoped, so a previous login's choice is not offered to whoever is signing in now.
    const currentTenantId = this._tenantService.getTenantSelectedThisSession();
    const selectedOption = this.environments.find(env => env.value === currentTenantId);
    if (selectedOption) {
      this.environmentControl.setValue(selectedOption);
    }
  }

  private async loadTenants(): Promise<void> {
    this.isLoading = true;
    try {
      const response = await firstValueFrom(this._tenantService.getTenants());

      this.mapToEnvironments(response);

      // If no tenants loaded from API, use JWT token tenant as fallback
      if (this.environments.length === 0) {
        const jwtTenant = this._authService.getTenantFromToken();
        if (jwtTenant) {
          this.environments = [
            {
              title: `${jwtTenant} (Default)`,
              value: jwtTenant
            }
          ];
          this.environmentControl.setValue(this.environments[0]);
        }
      }
    } catch (error) {
      console.error('Error loading tenants:', error);

      // On error, fallback to JWT token tenant
      const jwtTenant = this._authService.getTenantFromToken();
      if (jwtTenant) {
        this.environments = [
          {
            title: `${jwtTenant} (Default)`,
            value: jwtTenant
          }
        ];
        this.environmentControl.setValue(this.environments[0]);
      }
    } finally {
      this.isLoading = false;
    }
  }

  onConfirm(): void {
    if (this.environmentControl.valid) {
      this.modal.close(this.environmentControl.value);
    }
  }

  onCancel(): void {
    this.modal.close();
  }
}
